import React from "react";
import Layout, { useSEO } from "@/components/Layout";
import { useSiteContent } from "@/lib/useSiteContent";
import { Section, Overline, BlueRule, Divider, Button } from "@/components/primitives";
import PhilosophyFlow from "@/components/PhilosophyFlow";
import NewsletterSection from "@/components/NewsletterSection";

export default function About() {
  const site = useSiteContent();
  const f = site?.founder || {};
  useSEO({ title: "About | Impactful Mindset", description: "Impactful Mindset is a publishing and education platform for practical, psychologically grounded personal development." });
  return (
    <Layout>
      <Section className="pt-20 pb-10">
        <Overline>About</Overline>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4 leading-tight text-[#111827]">A serious platform for practical growth</h1>
        <BlueRule className="mt-6" />
        <p className="text-lg text-slate-600 mt-6 max-w-3xl leading-relaxed">
          Impactful Mindset 101 exists to help people think more clearly, make better decisions, act more
          intentionally, and grow personally and professionally—without empty motivation, exaggerated claims or guru
          language.
        </p>
      </Section>

      <Section className="py-14 bg-[#F8F9FA]">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-serif-display text-2xl font-semibold text-[#111827]">Our philosophy</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              We believe lasting change follows a sequence. It begins with awareness, moves through better thinking
              and better decisions, becomes intentional action, and—through repetition—turns into the consistent
              behavior that shapes character and growth.
            </p>
          </div>
          <div>
            <h2 className="font-serif-display text-2xl font-semibold text-[#111827]">Our approach</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Practical, psychologically credible, and honest. We take difficult realities seriously rather than
              papering over them with positivity. Growth here means responsibility, reflection and deliberate
              practice.
            </p>
          </div>
        </div>
        <div className="mt-12"><PhilosophyFlow /></div>
      </Section>

      <Section className="py-16">
        <Divider className="mb-12" />
        <div className="grid md:grid-cols-3 gap-10 items-start">
          <div className="aspect-[4/5] bg-slate-100 border border-slate-200 overflow-hidden max-w-xs">
            <img src={f.photo_url || "/brand/founder.jpg"} alt="Lance Cervantes, founder of Impactful Mindset 101" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-2">
            <Overline>The Founder</Overline>
            <h2 className="font-serif-display text-3xl font-semibold mt-3 text-[#111827]">Lance Cervantes</h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              Lance Cervantes is the founder, author and educator behind Impactful Mindset 101. His work focuses on
              practical ideas about mindset, leadership, discipline, personal growth and intentional living—grounded
              in reflection, responsibility and action rather than hype or shortcuts.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Through Impactful Mindset 101, he seeks to make personal development more thoughtful, realistic and
              useful: helping people develop greater awareness, think more clearly, make better decisions and
              translate those decisions into meaningful action.
            </p>
            <Button to="/join" className="mt-7" data-testid="about-join">Join Impactful Mindset</Button>
          </div>
        </div>
      </Section>

      <NewsletterSection />
    </Layout>
  );
}
